import React, { useState } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Row,
  Col,
  Alert,
  Card,
  CardBody,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import EventsList from "./EventsList";
import NewEvent from "./NewEvent";
import { fetchData, postData } from "../../services/apiServices";

const Events = () => {
  const [activeButton, setActiveButton] = useState(false);
  const [showGrok, setShowGrok] = useState(false);
  const [grokCategory, setGrokCategory] = useState("");
  const [grokIdea, setGrokIdea] = useState("");
  const [grokLoading, setGrokLoading] = useState(false);
  const [grokError, setGrokError] = useState("");
  const [grokResults, setGrokResults] = useState([]);
  const [grokPreviewIdx, setGrokPreviewIdx] = useState(null);
  const [categories, setCategories] = useState([]);

  // Fetch categories for dropdown
  React.useEffect(() => {
    fetchData("api/admin/categories").then((data) =>
      setCategories(data.data.categories || []),
    );
  }, []);

  const handleGrokOpen = () => {
    setShowGrok(true);
    setGrokCategory("");
    setGrokIdea("");
    setGrokResults([]);
    setGrokPreviewIdx(null);
    setGrokError("");
  };

  const handleGrokGenerate = async (e) => {
    e.preventDefault();
    setGrokLoading(true);
    setGrokError("");
    setGrokResults([]);
    setGrokPreviewIdx(null);
    try {
      // Find category name by _id
      const catObj = categories.find((cat) => cat._id === grokCategory);
      const genre = catObj ? catObj.name : grokCategory;
      const data = await postData("api/admin/grok/generatidea", {
        genre,
        eventIdea: grokIdea,
      });
      if (data.success && data.data && Array.isArray(data.data.events)) {
        setGrokResults(data.data.events);
      } else {
        setGrokError("No events generated. Try a different idea.");
      }
    } catch (err) {
      setGrokError("Failed to generate events. Please try again.");
    } finally {
      setGrokLoading(false);
    }
  };

  // Autofill NewEvent with selected grok event
  const handleGrokSelect = (idx) => {
    setGrokPreviewIdx(idx);
    setActiveButton(true);
    // Optionally: pass grokResults[idx] to NewEvent via props or context
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="mrmto" breadcrumbItem="Events" />
          {!activeButton && (
            <div>
              <div className="d-flex justify-content-end mb-3 gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGrokOpen}>
                  Generate Market
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setActiveButton(!activeButton)}>
                  New Event
                </button>
              </div>
              <EventsList />
            </div>
          )}

          {activeButton && (
            <NewEvent
              grokEvent={
                grokPreviewIdx !== null ? grokResults[grokPreviewIdx] : null
              }
              onBack={() => setActiveButton(false)}
            />
          )}

          {/* Generate Market Dialog */}
          <Modal isOpen={showGrok} toggle={() => setShowGrok(false)} size="lg">
            <ModalHeader toggle={() => setShowGrok(false)}>
              Generate Market Ideas
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={handleGrokGenerate}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="grok-category">Category</Label>
                      <Input
                        type="select"
                        id="grok-category"
                        value={grokCategory}
                        onChange={(e) => setGrokCategory(e.target.value)}
                        required>
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="grok-idea">Event Idea</Label>
                      <Input
                        type="text"
                        id="grok-idea"
                        value={grokIdea}
                        onChange={(e) => setGrokIdea(e.target.value)}
                        placeholder="Describe your event idea..."
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <div className="text-end">
                  <Button color="primary" type="submit" disabled={grokLoading}>
                    {grokLoading ? <Spinner size="sm" /> : "Generate"}
                  </Button>
                </div>
              </Form>
              {grokError && (
                <Alert color="danger" className="mt-3">
                  {grokError}
                </Alert>
              )}
              {grokResults.length > 0 && (
                <div className="mt-4">
                  <h5>Generated Events Preview</h5>
                  <Row>
                    {grokResults.map((ev, idx) => (
                      <Col md={6} key={idx} className="mb-3">
                        <Card
                          className={
                            grokPreviewIdx === idx ? "border-primary" : ""
                          }>
                          <CardBody>
                            <h6>{ev.event.event_title}</h6>
                            <div
                              className="mb-2 text-muted"
                              style={{ fontSize: 13 }}>
                              {ev.event.market_summary}
                            </div>
                            <div style={{ fontSize: 12 }}>
                              <b>Markets:</b>
                              <ul style={{ marginBottom: 0 }}>
                                {ev.markets.map((m, i) => (
                                  <li key={i}>
                                    {m.name}{" "}
                                    <span className="text-muted">
                                      ({m.start_date.split("T")[0]} to{" "}
                                      {m.end_date.split("T")[0]})
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="text-end mt-2">
                              <Button
                                size="sm"
                                color="primary"
                                outline
                                onClick={() => handleGrokSelect(idx)}>
                                Use This
                              </Button>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowGrok(false)}>
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Events;
